import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { EpdLinks } from 'routes/EpdRoutes';
import {
  CompanyFeatureFlagKey,
  CompanyFeatureFlagKeyValue,
  CompanyFeatureFlagStateModel,
  getAdminCompanyFeatureFlags,
  setAdminCompanyFeatureFlag,
} from 'services/api/adminEpdAccessApi';
import { QUERY_KEYS } from 'services/api/constants';
import styled from 'styled-components';
import { AdminBox, FixedLabel, H2 } from 'styles/Styles.styled';
import { getApiErrorDetail } from 'util/apiErrorUtils';

type Props = {
  companyId: string;
};

type SetFlagVariables = {
  key: CompanyFeatureFlagKeyValue;
  isEnabled: boolean;
};

const formatLastChange = (state: CompanyFeatureFlagStateModel | undefined) => {
  if (!state?.modifiedAt) {
    return 'Never changed';
  }

  const changedAt = new Date(state.modifiedAt).toLocaleString();
  return state.modifiedBy ? `Changed by ${state.modifiedBy} at ${changedAt}` : `Changed at ${changedAt}`;
};

/**
 * The company switches (Dynamic Forms, Read-only). System Administrator only - the caller renders it only for
 * useIsSystemAdmin(), and the endpoints refuse anyone else. Each checkbox saves at once; it is not part of the company form.
 */
const CompanyFeatureFlagsSection: React.FunctionComponent<Props> = ({ companyId }) => {
  const queryClient = useQueryClient();
  const queryKey = [QUERY_KEYS.ADMIN_COMPANY_FEATURE_FLAGS, companyId];

  const flagsQuery = useQuery({
    queryKey,
    queryFn: () => getAdminCompanyFeatureFlags(companyId),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
  });

  const setFlagMutation = useMutation({
    mutationFn: ({ key, isEnabled }: SetFlagVariables) => setAdminCompanyFeatureFlag(companyId, key, isEnabled),
    onSuccess: (flags) => {
      queryClient.setQueryData(queryKey, flags);
      // The dashboard and the other portal pages read the company switches through this key.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.COMPANY_FEATURE_FLAGS] });
      toast.success('The feature flag has been saved.', { position: 'top-center' });
    },
    onError: (error) => {
      toast.error(getApiErrorDetail(error) ?? 'Failed to save the feature flag.', { position: 'top-center' });
    },
  });

  const flags = flagsQuery.data;
  const isDisabled = !flags || setFlagMutation.isPending;

  const renderFlag = (
    id: string,
    label: string,
    key: CompanyFeatureFlagKeyValue,
    state: CompanyFeatureFlagStateModel | undefined
  ) => (
    <FlagRow>
      <FixedLabel htmlFor={id}>{label}</FixedLabel>
      <input
        id={id}
        type="checkbox"
        checked={state?.isEnabled ?? false}
        disabled={isDisabled}
        onChange={(event) => setFlagMutation.mutate({ key, isEnabled: event.target.checked })}
      />
      <LastChange>{flags ? formatLastChange(state) : ''}</LastChange>
    </FlagRow>
  );

  return (
    <>
      <H2>Feature flags</H2>
      <AdminBox style={{ backgroundColor: '#FDFDFD' }}>
        {flagsQuery.isError ? (
          <ErrorText>{getApiErrorDetail(flagsQuery.error) ?? 'Failed to load the feature flags.'}</ErrorText>
        ) : (
          <>
            {renderFlag(
              'companyFeatureFlagDynamicForms',
              'Dynamic Forms enabled',
              CompanyFeatureFlagKey.DynamicForms,
              flags?.dynamicForms
            )}
            {renderFlag('companyFeatureFlagReadOnly', 'Read-only', CompanyFeatureFlagKey.EpdReadOnly, flags?.readOnly)}
          </>
        )}
        <MigrationLinkRow>
          <Link to={EpdLinks.companyMigration(companyId)}>Open the migration screen</Link>
        </MigrationLinkRow>
      </AdminBox>
    </>
  );
};

const FlagRow = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.25rem 0;
`;

const LastChange = styled.span`
  color: #6b6b6b;
`;

const ErrorText = styled.div`
  color: ${(props) => props.theme.colors.statusLabelRedText};
  padding: 0.5rem 0;
`;

const MigrationLinkRow = styled.div`
  padding-top: 1rem;
`;

export default CompanyFeatureFlagsSection;
